import { EllipsisOutlined, FilterFilled } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Button, Card, Dropdown, Empty, Input, Space, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import AccessControl from '../../AccessControl';
import {
  DEFAULT_DATE_FORMAT,
  MODULES_KEY,
  PERMISSION_OBJECT,
  RELEASES_TABLE_FIELDS,
  ROUTES_MODULES_KEY,
} from '../../common/constants';
import { truncateWords } from '../../common/utils';
import CommonSelect from '../../components/CommonSelect';
import CommonTable from '../../components/CommonTable';
import VerifyPermissions from '../../components/VerifyPermissions';
import {
  mapAntTableChangeToParams,
  useTableQueryParams,
} from '../../hooks/useTableQueryParams';
import ReleaseModal from './components/ReleaseModal';
import { PROJECT_FOR_RELEASE, RELEASES } from './graphql/Queries';

const buildReleasesFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  projectIds: params?.projectIds?.length ? params?.projectIds : undefined,
  searchString: params?.releaseNumber,
  sortField: params?.sortField,
  sortOrder: params?.sortOrder,
});

function Releases(props) {
  const {
    location: { state },
  } = props;
  const history = useHistory();
  const { params, setParams } = useTableQueryParams({
    fields: RELEASES_TABLE_FIELDS,
  });
  const [projectSearchString, setProjectSearchString] = useState('');
  // Draft values for the Apply/Reset column filter popups — only committed
  // to the URL (and re-fetched) once "Apply"/"Search" is clicked.
  const [draftFilter, setDraftFilter] = useState({
    releaseNumber: params?.releaseNumber,
    projectIds: state?.projectIds?.length
      ? state?.projectIds
      : params?.projectIds,
  });
  const [filterVisible, setFilterVisible] = useState({
    releaseNumber: false,
    project: false,
  });
  const [showModal, setShowModal] = useState(false);
  const isAppBuildCreatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.APP_BUILDS,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });

  const [executeQuery, { loading, data }] = useLazyQuery(RELEASES, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  useEffect(() => {
    if (state?.projectIds?.length) {
      setParams({ projectIds: state?.projectIds, page: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    executeQuery({ variables: { filters: buildReleasesFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const releasesData = useMemo(() => data?.releases?.releases || [], [data]);
  const totalCount = useMemo(() => data?.releases?.count || 0, [data]);

  const clearFiltersAfterCreate = () => {
    setDraftFilter({ releaseNumber: '', projectIds: [] });
    setParams({ releaseNumber: '', projectIds: [], page: 1 });
  };

  const handleProjectSearch = debounce((value) => {
    setProjectSearchString(value?.toLowerCase());
  }, 500);

  const handleProjectSelect = (values) => {
    setDraftFilter((prev) => ({ ...prev, projectIds: values }));
    setProjectSearchString('');
    setParams({ projectIds: values, page: 1 });
  };

  const handleFilter = () => {
    setParams({ ...draftFilter, page: 1 });
    setFilterVisible({ releaseNumber: false, project: false });
  };

  const handleReset = (key) => {
    const patch =
      key === 'projectIds' ? { projectIds: [] } : { releaseNumber: '' };
    setDraftFilter((prev) => ({ ...prev, ...patch }));
    setParams({ ...patch, page: 1 });
    setFilterVisible((prev) => ({
      ...prev,
      [key === 'projectIds' ? 'project' : key]: false,
    }));
  };

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Input
          placeholder={`Search ${title}`}
          value={draftFilter?.[dataIndex]}
          onChange={(e) =>
            setDraftFilter((prev) => ({
              ...prev,
              [dataIndex]: e?.target?.value,
            }))
          }
          onPressEnter={handleFilter}
          className="filter-input-search"
        />
        <Space>
          <Button
            type="primary"
            onClick={handleFilter}
            size="small"
            className="filter-btn"
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(dataIndex)}
            size="small"
            className="filter-btn"
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: () => (
      <FilterFilled
        className={params?.[dataIndex] ? 'filter-icon-active' : ''}
      />
    ),
    filterDropdownOpen: filterVisible?.[dataIndex],
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible((prev) => ({ ...prev, [dataIndex]: visible })),
  });

  const getProjectFilterProps = () => ({
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <div className="mb-8">
          <CommonSelect
            placeholder="Select Project"
            allowClear
            query={PROJECT_FOR_RELEASE}
            onSearch={handleProjectSearch}
            onChange={(val) =>
              setDraftFilter((prev) => ({
                ...prev,
                projectIds: val ? [val] : [],
              }))
            }
            responsePath="projectsForRelease.projects"
            valuePath="id"
            labelPath="name"
            fetchPolicy="network-only"
            value={draftFilter?.projectIds?.[0]}
            variables={{
              filters: {
                searchString: projectSearchString,
                sortOrder: 'DESC',
                sortField: 'createdOn',
              },
            }}
            useEffectDeps={[projectSearchString]}
            isDataDependent={[projectSearchString]}
            filterOption={false}
            showSearch
            virtual={false}
          />
        </div>
        <Space>
          <Button
            type="primary"
            onClick={handleFilter}
            size="small"
            className="filter-btn"
          >
            Apply
          </Button>
          <Button
            onClick={() => handleReset('projectIds')}
            size="small"
            className="filter-btn"
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: () => (
      <FilterFilled
        className={params?.projectIds?.length ? 'filter-icon-active' : ''}
      />
    ),
    filterDropdownOpen: filterVisible.project,
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible((prev) => ({ ...prev, project: visible })),
  });

  const onChange = (pagination, _filters, sorter) => {
    setParams(mapAntTableChangeToParams(pagination, sorter, params?.pageSize));
  };

  const handleRowClick = (record) => {
    history.push(`${ROUTES_MODULES_KEY?.RELEASES}/${record?.id}`);
  };

  const handleViewDetails = (record) => {
    history.push(`${ROUTES_MODULES_KEY?.RELEASES}/${record?.id}`);
  };

  const handleCreateBuild = (record) => {
    history.push({
      pathname: `${ROUTES_MODULES_KEY?.APP_BUILDS}/create`,
      state: { releaseRecord: record },
    });
  };

  const columns = [
    {
      title: 'Release Number',
      dataIndex: 'releaseNumber',
      key: 'releaseNumber',
      width: 250,
      sorter: true,
      ...getColumnSearchProps('releaseNumber', 'Release Number'),
      render: (value, record) => (
        <Tooltip title={record?.releaseNumber} placement="topLeft">
          <Link
            to={`${ROUTES_MODULES_KEY?.RELEASES}/${record?.id}`}
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="release-number-truncate">
              {record?.releaseNumber}
            </div>
          </Link>
        </Tooltip>
      ),
    },
    {
      title: 'Project Name',
      dataIndex: 'projectName',
      key: 'projectName',
      width: 250,
      sorter: true,
      ...getProjectFilterProps(),
      render: (value, record) => (
        <Tooltip title={record?.projectName || '-'} placement="topLeft">
          <Link
            to={`${ROUTES_MODULES_KEY?.RELEASES}/${record?.id}`}
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="project-name-truncate">
              {record?.projectName || '-'}
            </div>
          </Link>
        </Tooltip>
      ),
    },
    {
      title: 'Release Date',
      dataIndex: 'releaseDate',
      key: 'releaseDate',
      sorter: true,
      render: (value, record) => (
        <Link
          to={`${ROUTES_MODULES_KEY?.RELEASES}/${record?.id}`}
          onClick={(e) => e?.stopPropagation()}
        >
          {record?.releaseDate
            ? dayjs(record?.releaseDate)?.format(DEFAULT_DATE_FORMAT)
            : '-'}
        </Link>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (value, record) => (
        <Link
          to={`${ROUTES_MODULES_KEY?.RELEASES}/${record?.id}`}
          onClick={(e) => e?.stopPropagation()}
        >
          {record?.createdAt
            ? dayjs(record?.createdAt)?.format(DEFAULT_DATE_FORMAT)
            : '-'}
        </Link>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (value, record) => {
        const truncated = truncateWords(record?.description);

        return (
          <Tooltip title={record?.description} placement="topLeft">
            <Link
              to={`${ROUTES_MODULES_KEY?.RELEASES}/${record?.id}`}
              onClick={(e) => e?.stopPropagation()}
            >
              <div className="description-truncate">{truncated}</div>
            </Link>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              { key: 'viewDetails', label: 'View Details' },
              ...(isAppBuildCreatePermission
                ? [{ key: 'createBuild', label: 'Create Build' }]
                : []),
            ],
            onClick: ({ key, domEvent }) => {
              domEvent?.stopPropagation();
              if (key === 'viewDetails') handleViewDetails(record);
              if (key === 'createBuild') handleCreateBuild(record);
            },
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            icon={<EllipsisOutlined />}
            onClick={(e) => e?.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="table-card-page">
      <Card
        title="Releases"
        className="ant-body-scroll"
        extra={
          <div className="project-filter-right">
            <div className="project-filter">
              <AccessControl
                modulekey={MODULES_KEY?.RELEASES}
                allowedPermissions={[PERMISSION_OBJECT?.create]}
              >
                <Button type="primary" onClick={() => setShowModal(true)}>
                  Create Release
                </Button>
              </AccessControl>
              <span className="release-select-tags">
                <CommonSelect
                  mode="multiple"
                  placeholder="All Projects"
                  allowClear
                  maxTagCount="responsive"
                  maxTagTextLength={10}
                  query={PROJECT_FOR_RELEASE}
                  onSearch={handleProjectSearch}
                  onChange={handleProjectSelect}
                  responsePath="projectsForRelease.projects"
                  valuePath="id"
                  labelPath="name"
                  fetchPolicy="network-only"
                  value={params?.projectIds}
                  variables={{
                    filters: {
                      searchString: projectSearchString,
                      sortOrder: 'DESC',
                      sortField: 'createdOn',
                    },
                  }}
                  useEffectDeps={[projectSearchString]}
                  isDataDependent={[projectSearchString]}
                  filterOption={false}
                  showSearch
                  virtual={false}
                />
              </span>
            </div>
          </div>
        }
      >
        <div className="card-body-wrapper">
          <CommonTable
            size="small"
            columns={columns}
            dataSource={[...releasesData]}
            locale={{
              emptyText: params?.projectIds?.length ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_DEFAULT}
                  description={
                    <div className="empty-state-container">
                      <div className="empty-state-title">No releases found</div>
                      <div className="empty-state-subtitle">
                        No releases match the selected project filter. Clear
                        filters to see all releases, or create a new one.
                      </div>
                    </div>
                  }
                />
              ) : undefined,
            }}
            onChange={onChange}
            rowKey={(obj) => obj?.id}
            loadingData={loading}
            bordered
            paginationConfig={{
              current: params?.page,
              total: totalCount,
              pageSize: params?.pageSize,
            }}
            onRow={(record) => ({ onClick: () => handleRowClick(record) })}
            rowClassName="releases-table-row"
            tableClassName="releases-table"
          />
        </div>
      </Card>
      {showModal && (
        <ReleaseModal
          show={showModal}
          close={() => setShowModal(false)}
          executeQuery={executeQuery}
          clearFiltersAfterCreate={clearFiltersAfterCreate}
        />
      )}
    </div>
  );
}

export default Releases;
